<template>
  <VdCard class="abtests-list-item-component" expandable size="small" :expanded="expanded">
    <div class="abtests-list-item-content">
      <section class="left">
        <VdTooltip orientation="top" content="1 is the highest priority">
          <div class="priority-number">
            <span class="label">Priority</span>
            <div class="number">
              {{ abtest.priority }}
            </div>
          </div>
        </VdTooltip>
        <div class="name">
          <h3 class="ab-test-title">
            <component :is="isDraft && inWipVersion ? 'router-link' : 'div'" class="title" :to="editLink">
              <span>
                <span>{{ abtest.name || "(unnamed)" }}</span>
                <span v-if="abtest.isImmutable">&nbsp;</span>
                <VdTooltip v-if="abtest.isImmutable" content="This A/B Test is immutable">
                  <vd-custom-icon
                    v-if="abtest.isImmutable"
                    name="lock-icon"
                    width="14"
                    box="0 0 10 14"
                    color="var(--vd-color-grey-light-1)"
                  />
                </VdTooltip>
              </span>
              <VdTooltip
                v-if="isPending || (isPublished && abtest.hasPendingChanges)"
                orientation="top"
                content="This A/B Test is waiting for publication"
              >
                <vd-custom-icon name="pending-icon" class="status-icon" />
              </VdTooltip>
              <VdTooltip v-else-if="isPublished" orientation="top" content="This A/B Test is currently running">
                <vd-custom-icon name="published-icon" class="status-icon" />
              </VdTooltip>

              <VdTooltip v-else-if="isTerminating" orientation="top" content="This A/B Test is waiting for termination">
                <VdIcon store="fa" name="folder-xmark" size="small" />
              </VdTooltip>
              &nbsp;
              <el-tag :key="abtest.id" :type="formattedAbtest.stateColor" size="small" round style="margin-right: 2px">
                {{ formattedAbtest.state }}
              </el-tag>
              <el-tag v-if="abtest.isDynamic && abtest.hasPendingChanges" size="small" round>pending changes</el-tag>
            </component>
          </h3>
          <div v-if="abtest.layer" class="layer-info">
            <VdIcon store="fas" name="layer-group" size="small" />
            <span>{{ abtest.layer.name }}</span>
          </div>
          <span v-if="abtest.createdOnVersion && abtest.createdOnVersion.humanId !== 'wip'" class="created-by">
            Created on version
            <span class="technical">{{ abtest.createdOnVersion.name }}</span>
          </span>
          <span v-if="abtest.stoppedOnVersion && abtest.stoppedOnVersion.humanId !== 'wip'" class="created-by">
            - Stopped on version
            <span class="technical">{{ abtest.stoppedOnVersion.name }}</span>
          </span>
        </div>
      </section>

      <section class="right">
        <AbtestAudience :segment-names="segmentsName" :platform="abtest.platform" :is-dynamic="abtest.isDynamic" />
        <DynamicAbSegments v-if="abtest.isDynamic" :dynamic-segments-name="dynamicSegmentsName" />
        <div class="numbers-section">
          <VdIcon store="fas" name="chart-pie" />
          <span class="numbers">
            <span>{{ totalCohortsAllocation }}%</span>
            <span>{{ abtest.userCount || "N/A" }}</span>
          </span>
        </div>
        <div class="remaining-time" :class="{ endless: !abtest.endsAt }">
          <VdIcon store="fas" name="calendar" />
          <span v-if="!abtest.endsAt">Endless</span>
          <span v-else>{{ daysLeft }}</span>
        </div>
        <vd-dropdown v-if="inWipVersion && !isArchived && canWriteAbtests">
          <vd-dropdown-item v-if="isDraft || isPublished || isTerminating" @click="onEdit">Edit</vd-dropdown-item>
          <vd-dropdown-item
            v-if="abtest.isDynamic && (isDraft || isPublished) && flags.addOfferToARunningAB.enabled"
            @click="onEditTreatment"
          >
            Edit treatment
          </vd-dropdown-item>
          <vd-dropdown-item v-if="!isTerminating && !isArchived" @click="onEditCohorts">Edit cohorts</vd-dropdown-item>
          <vd-dropdown-item v-if="isDraft && canBeMarkedAsReady" @click="onMarkAsReady">Mark as ready</vd-dropdown-item>
          <vd-dropdown-item v-if="isPending" @click="onMarkAsDraft">Mark as draft</vd-dropdown-item>
          <vd-dropdown-item v-if="isPublished" dangerous @click="onMarkAsTerminating">
            Stop this A/B Test
          </vd-dropdown-item>
          <vd-dropdown-item v-if="isTerminating" dangerous @click="onMarkAsCancelTerminating">
            Cancel A/B Test termination
          </vd-dropdown-item>
          <vd-dropdown-item v-if="isDraft" dangerous @click="onDelete">Delete</vd-dropdown-item>
          <vd-dropdown-item v-if="!abtest.isDynamic" @click="onDuplicate">Duplicate</vd-dropdown-item>
        </vd-dropdown>
      </section>
    </div>
    <template #expanded-content>
      <AbtestDetails
        class="abtest-details"
        :abtest="abtest"
        :game-id="gameId"
        :version-id="versionId"
        :archived="isArchived"
        :show-test-button="showTestButton"
        :offers-by-id="offersById"
        :campaign-names-by-id="campaignNamesById"
        @attach-tester="onAttachTester"
      />
    </template>
  </VdCard>
</template>

<script setup lang="ts">
/**
 * This component represents a game's ABTest displayed in a list.
 * ABTestesListItem is use in ABTestesListPage and ABTestesCatalogImportPage
 */
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { VdCard, VdDropdown, VdDropdownItem, VdTooltip } from "@voodoo.io/voodoo-ui";
import { parseISO } from "date-fns";
import formatDistanceToNow from "date-fns/formatDistanceToNow/index";
import { PropType, computed } from "vue";
import { RouteLocationRaw } from "vue-router";

import { ABTEST_STATES_COLOR_MAPPING } from "@/constants";
import { AbtestFragment, AbtestState } from "@/graphql";
import usePermissions from "@/hooks/use-permissions";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import AbtestAudience from "@/pages/games/GameDisplay/children/abtests/components/AbtestAudience.vue";
import { TreatmentOfferSummary } from "@/modules/Offers/hooks/useTreatmentOffersData";
import AbtestDetails from "@/pages/games/GameDisplay/children/abtests/components/AbtestDetails.vue";
import DynamicAbSegments from "@/pages/games/GameDisplay/children/abtests/components/DynamicAbSegments.vue";
import links from "@/router/links";

interface DynamicAbtest {
  isDynamic: boolean;
  dynamicSegments: { id: string; name: string }[];
  hasPendingChanges: boolean;
}

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },

  versionId: {
    type: String,
    required: true,
  },

  /**
   * The actual abtest object
   */
  abtest: {
    type: Object as PropType<AbtestFragment & DynamicAbtest>,
    required: true,
  },

  /**
   * If true, the card is expanded
   */
  expanded: {
    type: Boolean,
    default: false,
  },

  /**
   * True if the button "test" should be displayed in each cohort
   */
  showTestButton: {
    type: Boolean,
    default: false,
  },

  inWipVersion: {
    type: Boolean,
    required: true,
  },

  // Offer/campaign lookups fetched once at page level (useTreatmentOffersData), forwarded to AbtestDetails.
  offersById: {
    type: Object as PropType<Record<string, TreatmentOfferSummary>>,
    default: () => ({}),
  },
  campaignNamesById: {
    type: Object as PropType<Record<string, string>>,
    default: () => ({}),
  },
});

const emit = defineEmits([
  "delete",
  "edit",
  "edit-treatment",
  "edit-cohorts",
  "ready",
  "draft",
  "terminating",
  "cancel-terminating",
  "attach-tester",
  "duplicate",
]);

const { canWriteAbtests } = usePermissions();

const isDraft = computed(() => props.abtest.state === AbtestState.Draft);
const isPending = computed(() => props.abtest.state === AbtestState.Pending);
const isPublished = computed(() => props.abtest.state === AbtestState.Published);
const isTerminating = computed(() => props.abtest.state === AbtestState.Terminating);
const isArchived = computed(() => props.abtest.state === AbtestState.Archived);

const canBeMarkedAsReady = computed(() => props.abtest?.priority && props.abtest.priority > 0);

const formattedAbtest = computed(() => ({
  ...props.abtest,
  state: props.abtest.state.toLowerCase(),
  stateColor: ABTEST_STATES_COLOR_MAPPING[props.abtest.state],
}));

const { flags } = useFeatureFlags(props.gameId);

/**
 * Handle menu action
 */
const onDelete = () => {
  emit("delete", props.abtest.id);
};

const onEdit = () => {
  emit("edit", props.abtest.id);
};

const onEditTreatment = () => {
  emit("edit-treatment", props.abtest.id);
};

const onEditCohorts = () => {
  emit("edit-cohorts", props.abtest.id);
};

const onMarkAsReady = () => {
  emit("ready", props.abtest.id);
};

const onMarkAsDraft = () => {
  emit("draft", props.abtest.id);
};

const onMarkAsTerminating = () => {
  emit("terminating", props.abtest.id);
};

const onMarkAsCancelTerminating = () => {
  emit("cancel-terminating", props.abtest.id);
};

const onAttachTester = (cohortId: string) => {
  emit("attach-tester", cohortId);
};

const onDuplicate = () => {
  emit("duplicate", props.abtest.id);
};

/**
 * Some computations
 */
const daysLeft = computed(() => {
  if (!props.abtest.endsAt) {
    return null;
  }
  return formatDistanceToNow(new Date(parseISO(props.abtest.endsAt)), { addSuffix: true, includeSeconds: false });
});

const totalCohortsAllocation = computed(() =>
  props.abtest.cohorts.reduce((acc, { allocationRate }) => acc + allocationRate, 0)
);

const segmentsName = computed(() => props.abtest.segments.map(({ name }) => name));
const dynamicSegmentsName = computed(() => props.abtest.dynamicSegments.map(({ name }) => name));

/**
 * Links
 */
const editLink: RouteLocationRaw = {
  name: links.games.abtests.edit,
  params: {
    gameId: props.gameId,
    versionId: props.versionId,
    abtestId: props.abtest.id,
  },
};
</script>

<style lang="scss">
.abtests-list-item-component {
  margin: size("ML") 0 0;

  .dynamic-segments-wrapper {
    display: flex;
    font-size: 10px;

    svg {
      margin-right: 0.75rem;
    }

    .segments-column {
      display: flex;
      align-items: center;

      &:first-child {
        margin-right: 1.5rem;
      }
    }
  }

  .el-card__body {
    padding: size("M");
  }

  .technical {
    @extend %technical-name;
  }

  .test-button {
    margin-right: size("M");
  }

  .abtests-list-item-content {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .priority-number {
      @extend %priority-card;
      height: 50px;
      margin-right: size("M");

      .label {
        font-size: font("XS");
        padding: size("XS") size("M");
      }

      .number {
        font-size: font("M");
        padding: 0 size("XS") size("S") size("XS");
      }
    }

    .left,
    .right {
      display: flex;
      width: auto;
      align-items: center;
    }

    .right {
      .vertical-bar {
        @extend %vertical-bar;
      }

      .numbers-section {
        min-width: 60px;
        padding: 8px 12px;
        margin: 0 0.5rem;
      }

      .remaining-time {
        padding: 16px 12px;
      }

      .numbers-section,
      .remaining-time {
        display: flex;
        font-weight: 400;
        align-items: center;
        color: var(--vd-color-text-default);
        background-color: var(--vd-color-surface-100);
        height: 100%;
        border-radius: 16px;

        span {
          margin-left: 4px;
        }

        svg {
          font-size: 18px;
          color: var(--vd-color-text-default);
        }
      }

      .numbers {
        display: flex;
        flex-direction: column;

        span:nth-of-type(2) {
          font-size: font("S");
          color: $grey;
        }
      }

      .remaining-time {
        margin-right: size("ML");
      }
    }

    .left > .name {
      a {
        text-decoration: none;
      }

      h3.ab-test-title {
        margin-bottom: size("XXS");

        .el-tooltip {
          margin-left: size("XS");
        }

        .vd-icon {
          margin-left: 5px;
        }

        &:last-child {
          margin-bottom: 0;
          margin-top: -#{size("XS")};
        }

        a {
          color: $intense-blue;
        }

        .status-icon {
          position: relative;
          top: size("XS");
        }
      }

      .created-by {
        font-size: font("XS");
        color: $slate;
      }

      .layer-info {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        align-items: center;
        column-gap: 4px;
        font-size: font("S");

        svg {
          path {
            color: var(--vd-color-grey-light-1);
          }
        }
      }
    }
  }

  .abtest-details {
    margin-top: size("ML");
  }
}
</style>
